import { Icon, type IconName } from '@nexa/ui';

export interface FeatureRowShortcut {
  readonly icon: IconName;
  /** Tên trợ năng cho icon. Dịch được, nên nó tới từ copy chứ không nằm trong component. */
  readonly label: string;
  readonly key: string;
}

export interface FeatureRowProps {
  readonly icon: IconName;
  readonly term: string;
  readonly body: string;
  readonly shortcut?: FeatureRowShortcut;
}

/**
 * Một hàng tính năng ở cột trái `#collab` (`template.html` dòng 820–823).
 *
 * `items-start` cộng lề trên 2px cho icon: thiết kế đặt `margin-top:2px` để icon thẳng với DÒNG
 * CHỮ ĐẦU chứ không thẳng với tâm khối — khác biệt chỉ lộ ra khi mô tả xuống hai dòng ở khổ hẹp,
 * tức đúng lúc không ai đang nhìn.
 *
 * Dấu gạch ngang giữa `term` và `body` nằm trong component chứ không trong copy: nó là dấu phân
 * cách của LAYOUT, không phải nội dung dịch được.
 *
 * `shortcut` chỉ có ở MỘT hàng trong bốn. Thiết kế viết `Quick switch` cộng ký tự phím Command
 * U+2318 rồi tới `K`, nhưng U+2318 không có glyph trong bất kỳ file font nguồn nào của bundle —
 * đã quét cả 31 file `.woff2`. Không phải thiếu `unicode-range` (sinh lại font vá được) mà thiếu
 * ở chính file nguồn, nên không lượt `gen:fonts` nào cứu được; để nguyên thì nó rơi về font hệ
 * thống ngay giữa một dòng chữ. Quyết định của user 2026-08-19: vẽ bằng icon, giữ đúng thiết kế.
 *
 * Icon này CÓ `label`, khác với icon dẫn đầu hàng. Docblock của `IconProps.label` viết "đặt label
 * CHỈ khi icon là thông tin duy nhất" — ở đây đúng vậy: chữ quanh nó là `Quick switch` và `K`,
 * không chữ nào mang nghĩa "phím Command". Icon dẫn đầu thì ngược lại, nó lặp lại điều `term` đã
 * nói nên để trống.
 *
 * Cỡ 13 cho icon phím tắt là `chốt`, không phải `đo`: thiết kế dùng một KÝ TỰ ở cỡ chữ 14.5px chứ
 * không dùng icon, nên không có số để chép. 13 là bậc icon nhỏ có thật trong thiết kế (9 lượt) và
 * xấp xỉ chiều cao thân của ký tự gốc.
 */
export function FeatureRow({ icon, term, body, shortcut }: FeatureRowProps) {
  return (
    <div className="flex items-start gap-3">
      <Icon name={icon} size={17} className="mt-0.5 text-brand-600" />
      <div className="text-body text-ink-800">
        <b>
          {term}
          {shortcut !== undefined && (
            <>
              {' '}
              <Icon
                name={shortcut.icon}
                size={13}
                label={shortcut.label}
                className="inline align-middle"
              />
              {shortcut.key}
            </>
          )}
        </b>{' '}
        — <span>{body}</span>
      </div>
    </div>
  );
}
